/**
 * Booking form — full-screen multi-step overlay engine (vanilla).
 *
 * Rebuilt from the former Alpine widget. The backend submission contract is
 * FROZEN: the payload posted to /wp-json/cliniquelaprairie/v1/booking must keep
 * the same shape (buildPayload(), added in a later step).
 *
 * Markup:  widgets/booking-form/tpl/*  (block: .bookingform)
 * Styles:  assets/scss/components/bookingform.scss
 *
 * Built incrementally:
 *   Step 0 — data + step navigation       (done)
 *   Step 1 — Resort & Programs selection   (this step)
 *   Step 2 — Extra + slide-over            (todo)
 *   Step 3 — Rooms + carousel              (todo)
 *   Step 4 — Guest & Dates                 (todo)
 *   Step 5 — Personal details              (todo)
 *   Step 6 — Submit + thank-you            (todo)
 */

import { getUtmParams } from "./utility/UtmTracker";

const STEP_ORDER = ["resort", "extras", "rooms", "guests", "details"];

const esc = (value) =>
  String(value == null ? "" : value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char],
  );

class BookingForm {
  constructor(root) {
    this.root = root;
    this.stepsEl = root.querySelector("[data-bookingform-steps]");
    // Cache step sections + nav items by their key.
    this.sections = {};
    root.querySelectorAll("[data-step]").forEach((section) => {
      this.sections[section.dataset.step] = section;
    });

    this.navItems = {};
    root.querySelectorAll("[data-nav]").forEach((item) => {
      this.navItems[item.dataset.nav] = item;
    });

    // Feature flags + active step order. Extra step is disabled for now.
    this.withExtra = false;
    // this.stepOrder + nav visibility are set by applyStepOrder() below (once
    // this.fields exists) and recomputed whenever the programme changes, because
    // the "to be advised" programme skips the Rooms step.

    // Data passed from PHP via data-* attributes on the root.
    const data = root.dataset;
    this.maxGuests = parseInt(data.maxGuests, 10) || 1;
    this.resorts = this.parse(data.resorts);
    this.programmes = this.parse(data.programmes);
    // Optional "I don't know, I'd like to be advised" card (object or null).
    this.advised = this.parseObject(data.advised);
    this.extras = this.parse(data.extras);
    this.rooms = this.parse(data.rooms);
    this.closedDates = this.parse(data.closedDates);
    this.closedDatesChina = this.parse(data.closedDatesChina);
    this.closedDatesThailand = this.parse(data.closedDatesThailand);
    this.openingDateThailand = data.openingDateThailand || null;

    // Localised UI labels (optional; English fallbacks).
    this.t = (window.bookingForm && window.bookingForm.strings) || {};

    // Element refs for the resort step.
    this.resortsList = root.querySelector("[data-resorts-list]");
    this.programmesList = root.querySelector("[data-programmes-list]");
    this.programmesWrap = root.querySelector("[data-programmes-wrap]");

    // Element refs for the extras step + slide-over panel.
    this.extrasList = root.querySelector("[data-extras-list]");
    this.extrasEmpty = root.querySelector("[data-extras-empty]");
    this.panel = root.querySelector("[data-bookingform-panel]");
    this.panelItem = null;
    this.panelKind = null;
    this.roomsList = root.querySelector("[data-rooms-list]");
    this.guestsToggle = root.querySelector("[data-guests-toggle]");
    this.flexibleInput = root.querySelector("[data-flexible]");
    this.datePicker = null;
    this.guestsBlocks = root.querySelector("[data-guests-blocks]");
    this.guestTemplate = root.querySelector("#bookingform-guest-template");
    this.itis = [];

    // Runtime state.
    this.currentStep = null;
    this.guestCount = 1;
    this.submitting = false;
    this.lastPayload = null;
    this.fields = {
      resort: null,
      programme: null,
      extras: [],
      room: null,
      stay: {
        check_in: null,
        check_out: null,
        check_in_iso: null,
        check_out_iso: null,
      },
      datesUnknown: false,
    };

    this.applyStepOrder();
    this.bindNav();
    this.bindFooter();
    this.bindPanel();
    this.bindGuests();
    this.bindDetails();
    this.renderResorts();
    this.goTo("resort");
    this.preselectResortFromUrl();
  }

  // Active step order + nav visibility. The "to be advised" (TBD) programme has
  // no room selection, so the Rooms step is dropped from the flow when it's picked.
  computeStepOrder() {
    let order = this.withExtra
      ? STEP_ORDER
      : STEP_ORDER.filter((step) => step !== "extras");
    if (this.fields.programme && this.fields.programme.advised) {
      order = order.filter((step) => step !== "rooms");
    }
    return order;
  }

  applyStepOrder() {
    this.stepOrder = this.computeStepOrder();
    Object.entries(this.navItems).forEach(([step, item]) => {
      item.hidden = !this.stepOrder.includes(step);
    });
  }

  // Pre-select the resort matching the current destination page — e.g.
  // /destinations/switzerland/ selects the Switzerland resort. The destination
  // URL slug is matched against each resort's location (country) or city, so no
  // hardcoded page→resort map is needed (data comes from the resort ACF fields).
  preselectResortFromUrl() {
    const match = window.location.pathname
      .toLowerCase()
      .match(/\/destinations\/([^/]+)/);
    if (!match) return;

    const slug = match[1];
    const slugify = (value) =>
      String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

    const resort = this.resorts.find((item) => {
      const tokens = [slugify(item.location), slugify(item.city)].filter(Boolean);
      return tokens.some((token) => slug === token || slug.includes(token));
    });

    if (resort) this.selectResort(resort.id, { scroll: false });
  }

  parse(value) {
    try {
      const parsed = JSON.parse(value || "null");
      return Array.isArray(parsed) ? parsed : parsed || [];
    } catch (error) {
      return [];
    }
  }

  // Parse a single object (or null) — used for the optional "advised" card.
  parseObject(value) {
    try {
      const parsed = JSON.parse(value || "null");
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch (error) {
      return null;
    }
  }

  label(key, fallback) {
    return this.t[key] || fallback;
  }

  // ---------------------------------------------------------------- navigation

  bindNav() {
    Object.entries(this.navItems).forEach(([step, item]) => {
      item.addEventListener("click", () => {
        if (item.classList.contains("bookingform__nav-item--disabled")) return;
        this.goTo(step);
      });
    });
  }

  bindFooter() {
    this.root.querySelectorAll("[data-next]").forEach((button) => {
      button.addEventListener("click", () => this.shift(button, 1));
    });
    this.root.querySelectorAll("[data-prev]").forEach((button) => {
      button.addEventListener("click", () => this.shift(button, -1));
    });
  }

  shift(button, direction) {
    const section = button.closest("[data-step]");
    if (!section) return;
    const index = this.stepOrder.indexOf(section.dataset.step);
    const target = this.stepOrder[index + direction];
    if (target) this.goTo(target);
  }

  goTo(step) {
    if (step !== this.currentStep && !this.canEnter(step)) return;

    this.currentStep = step;

    Object.values(this.sections).forEach((section) => {
      section.hidden = section.dataset.step !== step;
    });

    if (step === "extras") this.renderExtras();
    if (step === "rooms") this.renderRooms();
    if (step === "guests") this.enterGuests();
    if (step === "details") this.renderGuests();

    if (this.stepsEl) this.stepsEl.scrollTop = 0;
    this.refresh();
  }

  isStepValid(step) {
    switch (step) {
      case "resort":
        return !!this.fields.resort && !!this.fields.programme;
      case "extras":
        return true;
      case "rooms":
        return !!this.fields.room;
      case "guests":
        return (
          this.fields.datesUnknown ||
          (!!this.fields.stay.check_in && !!this.fields.stay.check_out)
        );
      case "details":
        return true;
      default:
        return true;
    }
  }

  canEnter(step) {
    const index = this.stepOrder.indexOf(step);
    for (let i = 0; i < index; i += 1) {
      if (!this.isStepValid(this.stepOrder[i])) return false;
    }
    return true;
  }

  refresh() {
    const currentIndex = this.stepOrder.indexOf(this.currentStep);

    this.stepOrder.forEach((step, index) => {
      const item = this.navItems[step];
      if (!item) return;

      const isActive = step === this.currentStep;
      item.classList.toggle("bookingform__nav-item--active", isActive);
      item.classList.toggle(
        "bookingform__nav-item--disabled",
        !isActive && !this.canEnter(step),
      );
      item.classList.toggle(
        "bookingform__nav-item--done",
        !isActive && index < currentIndex && this.isStepValid(step),
      );
    });

    const section = this.sections[this.currentStep];
    if (section) {
      const next = section.querySelector("[data-next]");
      if (next) next.disabled = !this.isStepValid(this.currentStep);
    }
  }

  // ------------------------------------------------------------ resort & programmes

  renderResorts() {
    if (!this.resortsList) return;

    this.resortsList.innerHTML = this.resorts
      .map(
        (resort) => `
            <button type="button" class="bookingform__card bookingform__card--resort" data-resort-id="${esc(resort.id)}">
                <span class="bookingform__card-media">
                    ${resort.image ? `<img class="bookingform__card-image" src="${esc(resort.image)}" alt="${esc(resort.location || resort.name)}" loading="lazy">` : ""}
                </span>
                <span class="bookingform__card-body">
                    <span class="bookingform__card-textwrap">
                        <span class="bookingform__card-title">${esc(resort.city)}, ${esc(resort.location || resort.name)}</span>
                        <span class="bookingform__card-text">${esc(resort.desc ? resort.desc : "")}</span>
                    </span>
                    <span class="bookingform__card-check" aria-hidden="true"></span>
                </span>
            </button>
        `,
      )
      .join("");

    this.resortsList.querySelectorAll("[data-resort-id]").forEach((card) => {
      card.addEventListener("click", () =>
        this.selectResort(card.dataset.resortId),
      );
    });
  }

  selectResort(id, options = {}) {
    const resort = this.resorts.find((item) => String(item.id) === String(id));
    if (!resort) return;

    const changed = !this.fields.resort || this.fields.resort.id !== resort.id;
    this.fields.resort = resort;

    if (changed) {
      // Resort drives programmes/rooms/dates — reset downstream choices.
      this.fields.programme = null;
      this.fields.extras = [];
      this.fields.room = null;
      this.fields.stay = {
        check_in: null,
        check_out: null,
        check_in_iso: null,
        check_out_iso: null,
      };
      this.fields.datesUnknown = false;
    }

    this.syncCards(this.resortsList, "resort-id", resort.id);
    this.renderProgrammes();
    if (this.programmesWrap) {
      this.programmesWrap.hidden = false;
      // Once a resort is picked, bring the programmes section into view.
      // Defer past layout (programmes were just rendered) for accurate offsets.
      // Skipped on URL pre-selection so the form opens at the top.
      if (options.scroll !== false) {
        window.setTimeout(() => this.scrollIntoView(this.programmesWrap), 30);
      }
    }
    this.refresh();
  }

  // Scroll an element to the top of the steps scroller.
  // The scroller has `scroll-behavior: smooth` in CSS, so setting scrollTop
  // animates without any JS-side timing race.
  scrollIntoView(target) {
    if (!target || !this.stepsEl) return;
    const top = Math.max(
      0,
      target.getBoundingClientRect().top
        - this.stepsEl.getBoundingClientRect().top
        + this.stepsEl.scrollTop
        - 24
    );
    this.stepsEl.scrollTop = top;
  }

  resortProgrammes() {
    if (!this.fields.resort) return [];
    return this.programmes.filter(
      (programme) =>
        Array.isArray(programme.resorts) &&
        programme.resorts.includes(this.fields.resort.id),
    );
  }

  renderProgrammes() {
    if (!this.programmesList) return;

    const programmes = this.resortProgrammes();
    const selectedId = this.fields.programme ? this.fields.programme.id : null;

    const cardsHtml = programmes
      .map((programme) => {
        const duration = [
          programme.duration_days,
          programme.duration_days_suffix
        ]
          .filter((part) => part != null && part !== "")
          .join(" ");

        const heroImage = programme.image || programme.icon || "";
        return `
                <article class="bookingform__card bookingform__card--programme" data-programme-id="${esc(programme.id)}" style="--bookingform-card-theme:${esc(programme.theme || "")}">
                    <div class="bookingform__card-media">
                        ${heroImage ? `<img class="bookingform__card-image" src="${esc(heroImage)}" alt="${esc(programme.name)}" loading="lazy">` : ""}
                    </div>
                    <div class="bookingform__card-body">
                    <div class="bookingform__program__top">
                        <h3 class="bookingform__card-title">${esc(programme.name)}</h3>

                        ${programme.description ? `<p class="bookingform__card-text">${esc(programme.description)}</p>` : ""}
                    </div>
                    <div class="bookingform__program__bottom">
                    <div class="bookingform__program__bottom__left">
                        ${programme.price ? `<p class="bookingform__card-price"><span>${esc(duration)}</span><span>${esc(programme.price)}</span></p>` : ""}
                        ${programme.information ? `<p class="bookingform__card-info">${esc(programme.information)}</p>` : ""}
                    </div>
                        <div class="bookingform__card-actions">
                            <button type="button" class="bookingform__card-cta" data-select>
                                <span data-cta-label>${esc(this.label("select", "Select"))}</span>
                            </button>
                        </div>
                           </div>
                    </div>
                </article>
            `;
      })
      .join("");

    // "I don't know, I'd like to be advised" — shown as a checkbox row (same
    // design as the flexible-dates toggle), image ignored.
    let advisedHtml = "";
    if (this.advised) {
      const on = selectedId === this.advised.id;
      advisedHtml = `
                <label class="bookingform__flexible bookingform__advised" data-advised-toggle>
                    <input type="checkbox" class="bookingform__flexible-input" data-advised-check${on ? " checked" : ""}>
                    <span class="bookingform__flexible-box" aria-hidden="true"><svg><use xlink:href="#bookingform-check"></use></svg></span>
                    <span class="bookingform__advised-text">
                        <span class="bookingform__flexible-label">${esc(this.advised.name)}</span>
                        ${this.advised.description ? `<span class="bookingform__advised-desc">${esc(this.advised.description)}</span>` : ""}
                    </span>
                </label>
            `;
    }

    this.programmesList.innerHTML = cardsHtml + advisedHtml;

    // Click a card to select that programme.
    this.programmesList.querySelectorAll("[data-programme-id]").forEach((card) => {
      card.setAttribute("role", "button");
      card.setAttribute("tabindex", "0");
      card.addEventListener("click", () => this.selectProgramme(card.dataset.programmeId));
      card.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.selectProgramme(card.dataset.programmeId);
        }
      });
    });

    // Advised checkbox: check = select it, uncheck = clear the programme choice.
    const advisedToggle = this.programmesList.querySelector("[data-advised-toggle]");
    if (advisedToggle) {
      const input = advisedToggle.querySelector("[data-advised-check]");
      input.addEventListener("change", () => {
        if (input.checked) {
          this.selectProgramme(this.advised.id);
        } else {
          this.clearProgramme();
        }
      });
    }

    if (selectedId) {
      this.syncCards(this.programmesList, "programme-id", selectedId);
    }
  }

  // Keep the advised checkbox in sync with the current programme selection.
  syncAdvisedCheckbox() {
    if (!this.programmesList) return;
    const input = this.programmesList.querySelector("[data-advised-check]");
    if (input) {
      input.checked = !!(this.fields.programme && this.fields.programme.advised);
    }
  }

  // Deselect the programme (e.g. unchecking the advised option).
  clearProgramme() {
    this.fields.programme = null;
    this.fields.room = null;
    this.fields.extras = [];
    this.applyStepOrder();
    this.syncCards(this.programmesList, "programme-id", null);
    this.syncAdvisedCheckbox();
    this.refresh();
  }

  selectProgramme(id) {
    let programme = this.resortProgrammes().find(
      (item) => String(item.id) === String(id),
    );
    // The "advised" card isn't resort-scoped — match it separately.
    if (!programme && this.advised && String(this.advised.id) === String(id)) {
      programme = this.advised;
    }
    if (!programme) return;

    const changed = !this.fields.programme || this.fields.programme.id !== programme.id;
    if (changed) this.fields.extras = []; // programme drives available extras

    this.fields.programme = programme;

    // The advised (TBD) programme skips the Rooms step — drop any chosen room.
    if (programme.advised) this.fields.room = null;

    // Recompute the flow (Rooms is skipped for the advised programme).
    this.applyStepOrder();

    // Programme change resets the date state; enterGuests() re-applies flexible
    // dates for an advised option that has no configured arrival days.
    if (changed) {
      this.fields.datesUnknown = false;
      if (this.flexibleInput) {
        this.flexibleInput.checked = false;
        this.flexibleInput.disabled = false;
      }
    }
    this.syncCards(this.programmesList, "programme-id", programme.id);
    this.syncAdvisedCheckbox();
    this.refresh();

    // Auto-advance to the next step (Rooms for a normal programme, Guests for TBD).
    const next = this.stepOrder[this.stepOrder.indexOf("resort") + 1];
    if (next && this.isStepValid("resort"))
      window.setTimeout(() => this.goTo(next), 250);
  }

  // ------------------------------------------------------------ extras + slide-over

  programmeExtras() {
    if (!this.fields.programme) return [];
    return this.extras.filter(
      (extra) =>
        Array.isArray(extra.programmes) &&
        extra.programmes.includes(this.fields.programme.id),
    );
  }

  renderExtras() {
    if (!this.extrasList) return;
    const extras = this.programmeExtras();

    if (this.extrasEmpty) this.extrasEmpty.hidden = extras.length > 0;

    this.extrasList.innerHTML = extras
      .map(
        (extra) => `
            <article class="bookingform__card bookingform__card--extra" data-extra-id="${esc(extra.id)}">
                <button type="button" class="bookingform__card-media bookingform__card-media--button" data-learn aria-label="${esc(extra.name)}">
                    ${extra.image ? `<img class="bookingform__card-image" src="${esc(extra.image)}" alt="${esc(extra.name)}" loading="lazy">` : ""}
                </button>
                <div class="bookingform__card-body">
                    <h3 class="bookingform__card-title">${esc(extra.name)}</h3>
                    <p class="bookingform__card-text bookingform__card-text--clamp">${esc(extra.description)}</p>
                    <button type="button" class="bookingform__card-link" data-learn>${esc(this.label("learn_more", "Learn more"))}</button>
                    <div class="bookingform__card-actions">
                        <button type="button" class="bookingform__card-cta" data-add>
                            <span data-cta-label>${esc(this.label("add", "Add"))}</span>
                        </button>
                    </div>
                </div>
            </article>
        `,
      )
      .join("");

    this.extrasList.querySelectorAll("[data-extra-id]").forEach((card) => {
      const id = card.dataset.extraId;
      card.querySelectorAll("[data-learn]").forEach((trigger) => {
        trigger.addEventListener("click", () => this.openPanel("extra", id));
      });
      const addBtn = card.querySelector("[data-add]");
      if (addBtn) addBtn.addEventListener("click", () => this.toggleExtra(id));
    });

    this.syncExtras();
  }

  isExtraSelected(id) {
    return this.fields.extras.some((extra) => String(extra.id) === String(id));
  }

  toggleExtra(id) {
    const extra = this.programmeExtras().find(
      (item) => String(item.id) === String(id),
    );
    if (!extra) return;

    const index = this.fields.extras.findIndex(
      (item) => String(item.id) === String(id),
    );
    if (index > -1) {
      this.fields.extras.splice(index, 1);
    } else {
      this.fields.extras.push(extra);
    }

    this.syncExtras();
  }

  // Reflect extras selection across the cards and the open panel.
  syncExtras() {
    if (this.extrasList) {
      this.extrasList.querySelectorAll(".bookingform__card").forEach((card) => {
        const on = this.isExtraSelected(card.dataset.extraId);
        card.classList.toggle("is-selected", on);
        const ctaLabel = card.querySelector("[data-cta-label]");
        if (ctaLabel)
          ctaLabel.textContent = on
            ? this.label("added", "Added")
            : this.label("add", "Add");
      });
    }
    if (this.panelKind === "extra" && this.panelItem) this.updatePanelButton();
  }

  bindPanel() {
    if (!this.panel) return;
    this.panel.hidden = false; // CSS controls visibility from here on

    this.panel.querySelectorAll("[data-panel-close]").forEach((element) => {
      element.addEventListener("click", () => this.closePanel());
    });

    const addBtn = this.panel.querySelector("[data-panel-add]");
    if (addBtn) {
      addBtn.addEventListener("click", () => {
        if (!this.panelItem) return;
        if (this.panelKind === "extra") {
          this.toggleExtra(this.panelItem.id);
        } else if (this.panelKind === "room") {
          this.selectRoom(this.panelItem.id);
          this.closePanel();
        }
      });
    }

    document.addEventListener("keyup", (event) => {
      if (
        event.key === "Escape" &&
        this.panel.classList.contains("bookingform__panel--open")
      ) {
        this.closePanel();
      }
    });
  }

  openPanel(kind, id) {
    if (!this.panel) return;

    let item;
    let images;
    let description;

    if (kind === "room") {
      item = this.resortRooms().find((room) => String(room.id) === String(id));
      if (!item) return;
      images =
        Array.isArray(item.gallery) && item.gallery.length
          ? item.gallery
          : (item.image ? [item.image] : []);
      description = item.description;
    } else {
      item = this.programmeExtras().find(
        (extra) => String(extra.id) === String(id),
      );
      if (!item) return;
      images = item.image ? [item.image] : [];
      description = item.description;
    }

    this.panelKind = kind;
    this.panelItem = item;

    const mediaEl = this.panel.querySelector("[data-panel-media]");
    const titleEl = this.panel.querySelector("[data-panel-title]");
    const textEl = this.panel.querySelector("[data-panel-text]");
    const metaEl = this.panel.querySelector("[data-panel-meta]");

    // Media: single <img> for a lone image, otherwise a carousel.
    if (mediaEl) {
      if (images.length > 1) {
        const slides = images
          .map(
            (src) => `<div class="bookingform__carousel-slide"><img class="bookingform__carousel-image" src="${esc(src)}" alt="${esc(item.name)}" loading="lazy"></div>`,
          )
          .join("");
        const dots = images
          .map(
            (unused, i) => `<button type="button" class="bookingform__carousel-dot${i === 0 ? " is-active" : ""}" data-carousel-dot="${i}" aria-label="${i + 1}"></button>`,
          )
          .join("");
        mediaEl.innerHTML = `
          <div class="bookingform__carousel" data-carousel>
            <div class="bookingform__carousel-track" data-carousel-track>${slides}</div>
            <button type="button" class="bookingform__carousel-arrow bookingform__carousel-arrow--prev" data-carousel-prev aria-label="${esc(this.label("previous", "Previous"))}"><svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none">
<path d="M5.72827 0.364136L0.728272 5.36414L5.72827 10.3641" stroke="white" stroke-width="1.03"/>
</svg></button>
            <button type="button" class="bookingform__carousel-arrow bookingform__carousel-arrow--next" data-carousel-next aria-label="${esc(this.label("next", "Next"))}"><svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none">
<path d="M0.364258 0.364136L5.36426 5.36414L0.364258 10.3641" stroke="white" stroke-width="1.03"/>
</svg></button>
            <div class="bookingform__carousel-dots">${dots}</div>
          </div>`;
        this.setupCarousel(mediaEl.querySelector("[data-carousel]"));
      } else if (images.length === 1) {
        mediaEl.innerHTML = `<img class="bookingform__panel-image" src="${esc(images[0])}" alt="${esc(item.name)}" loading="lazy">`;
      } else {
        mediaEl.innerHTML = "";
      }
    }

    if (titleEl) titleEl.textContent = item.name;
    if (textEl) textEl.textContent = description || "";

    // Meta: only for rooms (area + price note).
    if (metaEl) {
      if (kind === "room") {
        const rows = [];
        if (item.area) rows.push(`<span class="bookingform__room-area">${esc(item.area)}</span>`);
        if (item.price_note) rows.push(`<span>${esc(item.price_note)}</span>`);
        metaEl.innerHTML = rows.join("");
        metaEl.hidden = rows.length === 0;
      } else {
        metaEl.innerHTML = "";
        metaEl.hidden = true;
      }
    }

    this.updatePanelButton();
    this.panel.classList.add("bookingform__panel--open");
  }

  closePanel() {
    if (!this.panel) return;
    this.panel.classList.remove("bookingform__panel--open");
    this.panelItem = null;
    this.panelKind = null;
  }

  updatePanelButton() {
    const label =
      this.panel && this.panel.querySelector("[data-panel-add-label]");
    if (!label || !this.panelItem) return;

    if (this.panelKind === "room") {
      const selected =
        this.fields.room &&
        String(this.fields.room.id) === String(this.panelItem.id);
      label.textContent = selected
        ? this.label("selected", "Selected")
        : this.label("select_room", "Select");
    } else {
      label.textContent = this.isExtraSelected(this.panelItem.id)
        ? this.label("remove_extra", "Remove extra")
        : this.label("add_extra", "Add extra");
    }
  }

  // ------------------------------------------------------------ rooms + carousel

  resortRooms() {
    if (!this.fields.resort) return [];
    return this.rooms.filter(
      (room) =>
        Array.isArray(room.resorts) &&
        room.resorts.includes(this.fields.resort.id),
    );
  }

  renderRooms() {
    if (!this.roomsList) return;
    const rooms = this.resortRooms();

    this.roomsList.innerHTML = rooms
      .map((room) => {
        const images =
          Array.isArray(room.gallery) && room.gallery.length
            ? room.gallery
            : room.image
              ? [room.image]
              : [];

        const slides = images
          .map(
            (src) =>
              `<div class="bookingform__carousel-slide"><img class="bookingform__carousel-image" src="${esc(src)}" alt="${esc(room.name)}" loading="lazy"></div>`,
          )
          .join("");

        const arrows =
          images.length > 1
            ? `
                <button type="button" class="bookingform__carousel-arrow bookingform__carousel-arrow--prev" data-carousel-prev aria-label="${esc(this.label("previous", "Previous"))}"><svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none">
<path d="M5.72827 0.364136L0.728272 5.36414L5.72827 10.3641" stroke="white" stroke-width="1.03"/>
</svg></button>
                <button type="button" class="bookingform__carousel-arrow bookingform__carousel-arrow--next" data-carousel-next aria-label="${esc(this.label("next", "Next"))}"><svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none">
<path d="M0.364258 0.364136L5.36426 5.36414L0.364258 10.3641" stroke="white" stroke-width="1.03"/>
</svg></button>
            `
            : "";

        const dots =
          images.length > 1
            ? `<div class="bookingform__carousel-dots">${images.map((_, i) => `<button type="button" class="bookingform__carousel-dot${i === 0 ? " is-active" : ""}" data-carousel-dot="${i}" aria-label="${i + 1}"></button>`).join("")}</div>`
            : "";

        return `
                <article class="bookingform__room bookingform__card" data-room-id="${esc(room.id)}">
                    <div class="bookingform__carousel" data-carousel>
                        <div class="bookingform__carousel-track" data-carousel-track>${slides}</div>
                        ${arrows}
                        ${dots}
                    </div>
                    <div class="bookingform__room-body">
                    <div class="bookingform__program__top">
                        <h3 class="bookingform__card-title">${esc(room.name)}</h3>
                        <p class="bookingform__card-text bookingform__card-text--clamp" data-room-desc>${esc(room.description)}</p>
                        <button type="button" class="bookingform__card-link" data-room-more>${esc(this.label("learn_more", "Learn more"))}</button>
                    </div>
                        <div class="bookingform__program__bottom">
                        <div class="bookingform__room-meta">
                            ${room.area ? `<span class="bookingform__room-area">${esc(room.area)}</span>` : ""}
                            ${room.price_note ? `<span class="bookingform__room-price">${esc(room.price_note)}</span>` : ""}
                        </div>
                        <div class="bookingform__card-actions">
                            <button type="button" class="bookingform__card-cta" data-select-room>
                                <span data-cta-label>${esc(this.label("select", "Select"))}</span>
                            </button>
                        </div>
                        </div>
                    </div>
                </article>
            `;
      })
      .join("");

    this.roomsList.querySelectorAll("[data-room-id]").forEach((card) => {
      const id = card.dataset.roomId;

      // Click anywhere on the card selects, except inside the carousel (arrows
      // and dots) and the "Learn more" trigger (which opens the panel).
      card.setAttribute("role", "button");
      card.setAttribute("tabindex", "0");
      card.addEventListener("click", (event) => {
        if (event.target.closest("[data-carousel] button, [data-room-more]")) return;
        this.selectRoom(id);
      });
      card.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          if (event.target.closest("[data-room-more], [data-carousel] button")) return;
          event.preventDefault();
          this.selectRoom(id);
        }
      });

      const more = card.querySelector("[data-room-more]");
      if (more)
        more.addEventListener("click", () => this.openPanel("room", id));

      this.setupCarousel(card.querySelector("[data-carousel]"));
    });

    if (this.fields.room) this.syncRoomCards();
  }

  setupCarousel(carousel) {
    if (!carousel) return;
    const track = carousel.querySelector("[data-carousel-track]");
    const slides = carousel.querySelectorAll(".bookingform__carousel-slide");
    if (!track || slides.length <= 1) return;

    const dots = carousel.querySelectorAll("[data-carousel-dot]");
    let index = 0;

    const update = () => {
      track.style.transform = `translateX(-${index * 100}%)`;
      dots.forEach((dot, i) => dot.classList.toggle("is-active", i === index));
    };
    const go = (delta) => {
      index = (index + delta + slides.length) % slides.length;
      update();
    };

    const prev = carousel.querySelector("[data-carousel-prev]");
    const next = carousel.querySelector("[data-carousel-next]");
    if (prev) prev.addEventListener("click", () => go(-1));
    if (next) next.addEventListener("click", () => go(1));
    dots.forEach((dot, i) =>
      dot.addEventListener("click", () => {
        index = i;
        update();
      }),
    );

    update();
  }

  selectRoom(id) {
    const room = this.resortRooms().find(
      (item) => String(item.id) === String(id),
    );
    if (!room) return;
    this.fields.room = room;
    this.syncRoomCards();
    this.refresh();

    // Auto-advance to the next step (no Next click needed)
    const next = this.stepOrder[this.stepOrder.indexOf("rooms") + 1];
    if (next && this.isStepValid("rooms"))
      window.setTimeout(() => this.goTo(next), 250);
  }

  syncRoomCards() {
    if (!this.roomsList) return;
    const selectedId = this.fields.room ? this.fields.room.id : null;
    this.roomsList.querySelectorAll(".bookingform__room").forEach((card) => {
      const on = String(card.dataset.roomId) === String(selectedId);
      card.classList.toggle("is-selected", on);
      const ctaLabel = card.querySelector("[data-cta-label]");
      if (ctaLabel)
        ctaLabel.textContent = on
          ? this.label("selected", "Selected")
          : this.label("select", "Select");
    });
    if (this.panelKind === "room" && this.panelItem) this.updatePanelButton();
  }

  // ------------------------------------------------------------ guests & dates

  bindGuests() {
    if (this.flexibleInput) {
      this.flexibleInput.addEventListener("change", () =>
        this.toggleFlexible(this.flexibleInput.checked),
      );
    }

    // Rebuild the picker when the viewport crosses the mobile threshold,
    // so we switch between 1- and 2-month layouts on rotation/resize.
    this.lastCalendarMonths = this.calendarMonthCount();
    let resizeTimer = null;
    window.addEventListener("resize", () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        const next = this.calendarMonthCount();
        if (next !== this.lastCalendarMonths) {
          this.lastCalendarMonths = next;
          if (this.currentStep === "guests" && !this.fields.datesUnknown) {
            this.bindDatePicker();
          }
        }
      }, 150);
    });
  }

  // 1 month under 768px (mobile/portrait), 2 months above (default easepick layout).
  calendarMonthCount() {
    return window.matchMedia("(max-width: 767px)").matches ? 1 : 2;
  }

  enterGuests() {
    const label = this.root.querySelector("[data-max-guests-label]");
    if (label) label.textContent = this.maxGuests;

    this.renderGuestsToggle();

    const calendar = this.root.querySelector("[data-calendar]");
    const programme = this.fields.programme;
    const advised = !!(programme && programme.advised);
    // For the TBD option the arrival days are derived from the resort's programmes.
    const hasArrivalDays = advised
      ? this.advisedDateRules().arrival_days.length > 0
      : programme &&
        Array.isArray(programme.arrival_days) &&
        programme.arrival_days.length > 0;

    // Only when there are genuinely no arrival days available (e.g. a resort with
    // no programmes) do we fall back to flexible ("to be confirmed") dates.
    if (advised && !hasArrivalDays) {
      this.fields.datesUnknown = true;
      if (this.datePicker) {
        this.datePicker.destroy();
        this.datePicker = null;
      }
      if (this.flexibleInput) {
        this.flexibleInput.checked = true;
        this.flexibleInput.disabled = true;
      }
      if (calendar) calendar.classList.add("is-disabled");
      this.refresh();
      return;
    }

    if (this.flexibleInput) this.flexibleInput.disabled = false;
    if (!this.fields.datesUnknown) {
      if (calendar) calendar.classList.remove("is-disabled");
      this.bindDatePicker();
    }
  }

  renderGuestsToggle() {
    if (!this.guestsToggle) return;
    const max = Math.max(1, this.maxGuests);

    this.guestsToggle.innerHTML = Array.from({ length: max }, (unused, i) => {
      const count = i + 1;
      const noun =
        count > 1
          ? this.label("guests", "guests")
          : this.label("guest", "guest");
      return `<button type="button" class="bookingform__guests-option${count === this.guestCount ? " is-active" : ""}" data-guest-count="${count}">${count} ${esc(noun)}</button>`;
    }).join("");

    this.guestsToggle
      .querySelectorAll("[data-guest-count]")
      .forEach((button) => {
        button.addEventListener("click", () =>
          this.setGuestCount(parseInt(button.dataset.guestCount, 10)),
        );
      });
  }

  setGuestCount(count) {
    this.guestCount = count;
    if (this.guestsToggle) {
      this.guestsToggle
        .querySelectorAll("[data-guest-count]")
        .forEach((button) => {
          button.classList.toggle(
            "is-active",
            parseInt(button.dataset.guestCount, 10) === count,
          );
        });
    }
  }

  toggleFlexible(on) {
    this.fields.datesUnknown = on;
    const calendar = this.root.querySelector("[data-calendar]");

    if (on) {
      this.fields.stay = {
        check_in: null,
        check_out: null,
        check_in_iso: null,
        check_out_iso: null,
      };
      if (this.datePicker) {
        this.datePicker.destroy();
        this.datePicker = null;
      }
      if (calendar) calendar.classList.add("is-disabled");
    } else {
      if (calendar) calendar.classList.remove("is-disabled");
      this.bindDatePicker();
    }

    this.refresh();
  }

  // Date rules for the "to be advised" (TBD) programme, derived automatically
  // from every programme available at the selected resort: the union of their
  // arrival days, the smallest min-duration and the largest max-duration. This
  // lets the TBD calendar work without configuring dates on the advised option.
  advisedDateRules() {
    const programmes = this.resortProgrammes();
    const days = new Set();
    let min = Infinity;
    let max = 0;
    programmes.forEach((programme) => {
      (Array.isArray(programme.arrival_days) ? programme.arrival_days : []).forEach(
        (day) => days.add(day),
      );
      const pMin = parseInt(programme.duration_min_days, 10);
      const pMax = parseInt(programme.duration_max_days, 10);
      if (!Number.isNaN(pMin)) min = Math.min(min, pMin);
      if (!Number.isNaN(pMax)) max = Math.max(max, pMax);
    });
    const minDays = Number.isFinite(min) ? min : 1;
    return { arrival_days: [...days], minDays, maxDays: max > 0 ? max : minDays };
  }

  // easepick dual-month range picker — preserves the original date rules.
  bindDatePicker() {
    if (!this.fields.programme || typeof easepick === "undefined") return;

    const element = document.getElementById("bookingform-datepicker");
    if (!element) return;

    const programme = this.fields.programme;
    // The TBD option has no dates of its own — derive them from the programmes.
    let minDays;
    let maxDays;
    let arrivalDays;
    if (programme.advised) {
      const rules = this.advisedDateRules();
      minDays = rules.minDays;
      maxDays = rules.maxDays;
      arrivalDays = rules.arrival_days;
    } else {
      minDays = parseInt(programme.duration_min_days, 10) || 1;
      maxDays = parseInt(programme.duration_max_days, 10) || minDays;
      arrivalDays = Array.isArray(programme.arrival_days)
        ? programme.arrival_days
        : [];
    }

    const resortId = this.fields.resort ? this.fields.resort.id : "";
    let closedDays = this.closedDates;
    let minDate = new Date();

    if (resortId === "clinique-la-prairie-health-resort-montreux") {
      closedDays = this.closedDates;
    } else if (resortId === "clinique-la-prairie-health-resort-phuket") {
      closedDays = this.closedDatesThailand;
      if (this.openingDateThailand) {
        const parts = this.openingDateThailand.split("/");
        const parsed = new Date(parts[2], parts[1] - 1, parts[0]);
        if (parsed > minDate) minDate = parsed;
      }
    } else {
      closedDays = this.closedDatesChina;
    }

    if (this.datePicker) {
      this.datePicker.destroy();
      this.datePicker = null;
    }
    if (arrivalDays.length === 0) return; // incompatible programme → no picker

    const self = this;
    // The whole stay length is fixed by the programme: picking an arrival date
    // auto-fills the departure so the guest never selects the end date. The
    // programme duration is expressed in DAYS, counted inclusively (a 7-day
    // programme = check-in + 6 nights → e.g. Sunday arrival, Saturday departure),
    // so the date offset is maxDays - 1 (mirrors $minNights = $minDays - 1 in
    // booking-form.php). For fixed-length programmes minDays === maxDays.
    const stayLength = Math.max(1, maxDays - 1);

    this.datePicker = new easepick.create({
      element,
      css: [
        "https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css",
        "/wp-content/themes/cn-theme-cliniquelaprairie/widgets/booking-form/css/easepick.css",
      ],
      inline: true,
      calendars: this.calendarMonthCount(),
      grid: this.calendarMonthCount(),
      format: "DD/MM/YYYY",
      plugins: ["LockPlugin", "RangePlugin"],
      LockPlugin: {
        minDate,
        minDays,
        maxDays,
        filter(date, picked) {
          const formatted = date.format("DD/MM/YYYY");
          if (closedDays.includes(formatted)) return true;
          if (picked.length === 1) {
            return date < picked[0];
          }
          const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
          return !arrivalDays.includes(days[date.getDay()]);
        },
      },
      setup(picker) {
        // Safety net: if the capture-phase handler below ever fails to stop the
        // native two-step selection, still record whatever range was picked.
        picker.on("select", (event) => {
          const { start, end } = event.detail;
          self.setStay(start, end);
        });
      },
    });

    // Single-click stay: easepick binds its day-click handler on ui.container in
    // the bubble phase. We add a capture-phase listener on the same node so we
    // run first, compute the end ourselves and stopPropagation() to short-circuit
    // the native check-in / check-out two-step selection.
    const container = this.datePicker.ui && this.datePicker.ui.container;
    if (container) {
      container.addEventListener(
        "click",
        (event) => {
          const cell = event.target.closest(".day[data-time]");
          if (!cell || cell.classList.contains("locked")) return;
          event.stopPropagation();
          const start = new Date(Number(cell.dataset.time));
          const end = new Date(start);
          end.setDate(end.getDate() + stayLength);
          self.datePicker.setDateRange(start, end);
          self.setStay(start, end);
        },
        true,
      );
    }

    if (minDate > new Date()) this.datePicker.gotoDate(minDate);
  }

  // Record the selected stay (shared by the auto-select click handler and the
  // easepick "select" safety net). Keeps the original ISO/locale formatting.
  setStay(start, end) {
    const stay = this.fields.stay;
    stay.check_in_iso = new Date(start).toISOString().substring(0, 10);
    stay.check_out_iso = new Date(end).toISOString().substring(0, 10);
    stay.check_in = new Date(start).toLocaleDateString();
    stay.check_out = new Date(end).toLocaleDateString();
    this.refresh();
  }

  // ------------------------------------------------------------ personal details

  renderGuests() {
    if (!this.guestsBlocks || !this.guestTemplate) return;

    // Keep entered values when the count is unchanged.
    const existing = this.guestsBlocks.querySelectorAll(
      ".bookingform__guest",
    ).length;
    if (existing === this.guestCount) return;

    this.guestsBlocks.innerHTML = "";
    this.itis = [];

    for (let i = 0; i < this.guestCount; i += 1) {
      const fragment = this.guestTemplate.content.cloneNode(true);
      const number = fragment.querySelector("[data-guest-number]");
      if (number) number.textContent = i + 1;
      this.guestsBlocks.appendChild(fragment);
    }

    this.guestsBlocks
      .querySelectorAll(".bookingform__guest")
      .forEach((block, index) => {
        this.setupGuestBlock(block, index);
      });
  }

  setupGuestBlock(block, index) {
    // The per-guest fields sit in a <form> (enables browser autofill of saved
    // contact details). Block its native submit so Enter never reloads the page —
    // the booking is sent via the Submit button (fetch).
    const form = block.querySelector("form");
    if (form) form.addEventListener("submit", (event) => event.preventDefault());

    // Phone: the dial code is a plain native <select> (data-field
    // telephone_country_code, value = dial digits) beside the number input — see
    // booking-form-dialcode-options.php. Replaced intl-tel-input, whose dropdown
    // was clipped/unusable inside the offcanvas.
    // The closed select shows only the dial code via an overlay label (the select
    // text itself is transparent in CSS); the dropdown still lists the countries.
    const codeSelect = block.querySelector('[data-field="telephone_country_code"]');
    const codeLabel = block.querySelector("[data-phone-code-label]");
    if (codeSelect && codeLabel) {
      const syncCode = () => {
        codeLabel.textContent = codeSelect.value ? "+" + codeSelect.value : "";
      };
      syncCode();
      codeSelect.addEventListener("change", syncCode);
    }

    // Country: searchable Tom Select on desktop only. On mobile it stays a plain
    // native <select> (iOS opens its wheel picker): Tom Select focuses a real text
    // input, and on iOS the on-screen keyboard inside the position:fixed offcanvas
    // shifts/enlarges the whole form (reported by the client on a real iPhone).
    // The element already carries every country option, so skipping the
    // enhancement needs no markup change and keeps the same value/validation.
    const country = block.querySelector('[data-field="country"]');
    const enhanceCountry =
      typeof window.TomSelect !== "undefined" &&
      window.matchMedia("(min-width: 768px)").matches;
    if (country && enhanceCountry) {
      try {
        new window.TomSelect(country, {
          create: false,
          allowEmptyOption: true,
        });
      } catch (error) {
        console.error("[bookingform] country init failed", error);
      }
    }

    // Date of birth uses the browser's native date picker (<input type="date">),
    // so no JS enhancement is needed. Value is YYYY-MM-DD, which the CRM's
    // formatDate() accepts.

    // Conditional "other" field
    const how = block.querySelector('[data-field="how_did_you_hear_about_us"]');
    const other = block.querySelector("[data-conditional-other]");
    if (how && other) {
      const sync = () => {
        const show = [
          "Magazine/Newspaper article",
          "Other",
        ].includes(how.value);
        other.hidden = !show;
      };
      how.addEventListener("change", sync);
      sync();
    }

    // Inline validation on blur/change
    block.querySelectorAll("[data-rules]").forEach((field) => {
      const eventName = field.tagName === "SELECT" ? "change" : "blur";
      field.addEventListener(eventName, () => this.validateField(field));
    });
  }

  parseRules(json) {
    try {
      const rules = JSON.parse(json || "[]");
      return Array.isArray(rules) ? rules : [];
    } catch (error) {
      return [];
    }
  }

  checkRule(rule, value) {
    switch (rule) {
      case "required":
        return (
          value !== "" &&
          value !== null &&
          value !== false &&
          value !== undefined
        );
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value));
      case "numeric": {
        const digits = String(value).replace(/[\s()+.-]/g, "");
        // Format-only rule: an empty value is left to the "required" rule, so
        // optional numeric fields (e.g. the phone number) validate when blank.
        if (digits === "") return true;
        return /^[0-9]+$/.test(digits);
      }
      case "truthy":
        return (
          value === true ||
          value === "true" ||
          value === 1 ||
          value === "1" ||
          value === "on"
        );
      default:
        return true;
    }
  }

  // Validate one field (vanilla replacement for Iodine). Keeps .js-field-error + .has-error.
  validateField(field) {
    const rules = this.parseRules(field.dataset.rules);
    if (!rules.length) return true;

    const wrapper = field.closest("[data-handle]") || field.parentElement;
    if (wrapper && wrapper.hidden) return true; // skip hidden conditional fields

    const errorEl = wrapper ? wrapper.querySelector(".js-field-error") : null;
    const value = field.type === "checkbox" ? field.checked : field.value;
    const messages = (window.bookingForm && window.bookingForm.validator) || {};

    let error = "";
    for (let i = 0; i < rules.length; i += 1) {
      if (!this.checkRule(rules[i], value)) {
        error = messages[rules[i]] || "Invalid";
        break;
      }
    }

    if (wrapper) wrapper.classList.toggle("has-error", !!error);
    if (errorEl) errorEl.textContent = error;
    return !error;
  }

  // ------------------------------------------------------------ submit & thank-you

  bindDetails() {
    this.termsInput = this.root.querySelector("[data-terms]");
    this.newsletterInput = this.root.querySelector("[data-newsletter]");
    this.summaryEl = this.root.querySelector("[data-summary]");

    const submit = this.root.querySelector("[data-submit]");
    if (submit) submit.addEventListener("click", () => this.submit());

    // "Back to home" uses its native href (the home page) — no JS override so it
    // no longer redirects to the submission-redirect URL (e.g. /contact/thank-you/).

    if (this.termsInput) {
      this.termsInput.addEventListener("change", () =>
        this.validateField(this.termsInput),
      );
    }
  }

  validateDetails() {
    const fields = this.sections.details.querySelectorAll("[data-rules]");
    let firstInvalid = null;
    fields.forEach((field) => {
      if (!this.validateField(field) && !firstInvalid) firstInvalid = field;
    });

    if (firstInvalid) {
      const wrapper = firstInvalid.closest("[data-handle]") || firstInvalid;
      if (this.stepsEl) {
        this.stepsEl.scrollTo({
          top: Math.max(0, wrapper.offsetTop - 80),
          behavior: "smooth",
        });
      }
      return false;
    }
    return true;
  }

  // Build the FROZEN payload posted to /wp-json/cliniquelaprairie/v1/booking.
  buildPayload() {
    const blocks = this.guestsBlocks
      ? this.guestsBlocks.querySelectorAll(".bookingform__guest")
      : [];
    const guests = [];

    blocks.forEach((block, index) => {
      const raw = {};
      block.querySelectorAll("[data-field]").forEach((field) => {
        raw[field.dataset.field] = field.value;
      });

      // Phone: dial code (digits, no +) from the native country-code select +
      // national digits from the number input.
      const dialCode = (raw.telephone_country_code || "").replace(/\D/g, "");
      const nationalNumber = (raw.telephone_number || "").replace(/\D/g, "");

      guests.push({
        id: index + 1,
        contact: {
          title: raw.title || "",
          first_name: raw.first_name || "",
          last_name: raw.last_name || "",
          address: raw.address || "",
          postal_code: raw.postal_code || "",
          city: raw.city || "",
          country: raw.country || "",
          telephone_country_code: dialCode,
          telephone_number: nationalNumber,
          email_address: raw.email_address || "",
          date_of_birth: raw.date_of_birth || "",
          how_did_you_hear_about_us: raw.how_did_you_hear_about_us || "",
          how_did_you_hear_about_us_other:
            raw.how_did_you_hear_about_us_other || "",
        },
        programme: this.fields.programme,
        extras: this.fields.extras,
        special_requirements: raw.special_requirements || "",
      });
    });

    return {
      resort: this.fields.resort,
      guests,
      rooms: this.fields.room ? [this.fields.room] : [],
      stay: this.fields.datesUnknown
        ? { check_in: "", check_out: "", check_in_iso: "", check_out_iso: "" }
        : this.fields.stay,
      accepted_terms: this.termsInput ? this.termsInput.checked : false,
      receive_marketing: this.newsletterInput
        ? this.newsletterInput.checked
        : false,
      page_url: window.location.href,
      utm_params: getUtmParams(),
      dates_unknown: this.fields.datesUnknown,
    };
  }

  // Anti-spam token for the booking endpoint. The booking form is not a Gravity
  // Form, so it carried no protection at all; we reuse the site's reCAPTCHA v3
  // keys (see CN_Booking_Form::recaptcha_site_key). Returns an empty string when
  // the key is absent or the script failed to load — the server then falls back
  // to allowing the request rather than losing a real booking.
  async recaptchaToken() {
    const key = this.root.dataset.recaptchaKey;
    if (!key || typeof window.grecaptcha === "undefined") return "";

    try {
      await new Promise((resolve) => window.grecaptcha.ready(resolve));
      return await window.grecaptcha.execute(key, { action: "booking" });
    } catch (error) {
      console.error("[bookingform] captcha failed", error);
      return "";
    }
  }

  async submit() {
    if (this.submitting) return;
    if (!this.validateDetails()) return;

    this.submitting = true;
    this.root.classList.add("bookingform--submitting");

    const submitBtn = this.root.querySelector("[data-submit]");
    const submitLabel = submitBtn ? submitBtn.querySelector(".bookingform__action-label") : null;
    const originalLabel = submitLabel ? submitLabel.textContent : "";
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute("aria-busy", "true");
      submitBtn.classList.add("is-loading");
    }
    if (submitLabel) submitLabel.textContent = this.label("submitting", "Submitting…");

    const payload = this.buildPayload();
    payload.recaptcha_token = await this.recaptchaToken();
    this.lastPayload = payload;

    // Run the request alongside a minimum delay so the loading-logo fill is
    // always seen, even when the endpoint responds instantly.
    // The endpoint is a rest_pre_dispatch filter; it sends to the CRM and the
    // response is intentionally ignored (it returns a non-2xx after processing).
    await Promise.all([
      fetch("/wp-json/cliniquelaprairie/v1/booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch((error) => console.error("Booking submit error:", error)),
      new Promise((resolve) => window.setTimeout(resolve, 900)),
    ]);

    this.submitting = false;
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.removeAttribute("aria-busy");
      submitBtn.classList.remove("is-loading");
    }
    if (submitLabel) submitLabel.textContent = originalLabel;

    // Reveal the thank-you screen beneath the loader, then fade the loader out.
    this.renderThankYou();
    this.showThankYou();
    this.root.classList.remove("bookingform--submitting");
  }

  showThankYou() {
    Object.values(this.sections).forEach((section) => {
      section.hidden = section.dataset.step !== "thankyou";
    });
    this.currentStep = "thankyou";
    this.root.classList.add("is-thankyou");
    const nav = this.root.querySelector("[data-bookingform-nav]");
    if (nav) nav.style.display = "none";
    if (this.stepsEl) this.stepsEl.scrollTop = 0;
  }

  renderThankYou() {
    if (!this.summaryEl) return;
    const payload = this.lastPayload || this.buildPayload();

    const guestNames = payload.guests
      .map(
        (guest, i) =>
          `${esc(this.label("guest", "Guest"))} ${i + 1}: <strong>${esc(`${guest.contact.first_name} ${guest.contact.last_name}`.trim() || "—")}</strong>`,
      )
      .join("<br>");

    const programs = payload.guests
      .map(
        (guest, i) =>
          `${esc(this.label("guest", "Guest"))} ${i + 1}: <strong>${esc(guest.programme ? guest.programme.name : "—")}</strong>`,
      )
      .join("<br>");

    const extras = this.fields.extras.length
      ? this.fields.extras.map((extra) => esc(extra.name)).join(", ")
      : this.label("not_selected", "Not selected");

    const room = this.fields.room
      ? `${esc(this.label("selected_colon", "Selected:"))} <strong>${esc(this.fields.room.name)}</strong>`
      : this.label("not_selected", "Not selected");

    const stay = payload.stay || {};
    const dates = payload.dates_unknown
      ? `<strong>${esc(this.label("flexible_dates", "Stay dates: To be confirmed"))}</strong>`
      : stay.check_in
        ? `<strong>${esc(stay.check_in)} – ${esc(stay.check_out)}</strong>`
        : this.label("not_selected", "Not selected");

    const row = (label, value) =>
      `<div class="bookingform__summary-row"><span class="bookingform__summary-label">${esc(label)}</span><span class="bookingform__summary-value">${value}</span></div>`;

    this.summaryEl.innerHTML = [
      row(this.label("number_of_guests", "Number of guests"), guestNames),
      row(this.label("programs", "Programs"), programs),
      //row(this.label("additional_extras", "Additional extras"), extras),
      row(this.label("rooms", "Rooms"), room),
      row(this.label("date_of_stay", "Dates of stay"), dates),
    ].join("");
  }

  // Toggle .is-selected + the CTA label across a card list.
  syncCards(listEl, attr, selectedId) {
    if (!listEl) return;
    listEl.querySelectorAll(".bookingform__card").forEach((card) => {
      const on = String(card.dataset[this.camel(attr)]) === String(selectedId);
      card.classList.toggle("is-selected", on);
      const ctaLabel = card.querySelector("[data-cta-label]");
      if (ctaLabel) {
        ctaLabel.textContent = on
          ? this.label("selected", "Selected")
          : this.label("select", "Select");
      }
    });
  }

  camel(attr) {
    return attr.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
  }
}

const initBookingForms = () => {
  document.querySelectorAll(".bookingform").forEach((root) => {
    if (root.dataset.bookingformReady) return;
    root.dataset.bookingformReady = "true";
    root._bookingform = new BookingForm(root);
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBookingForms);
} else {
  initBookingForms();
}

export default BookingForm;
